import { extendTheme, type ThemeConfig } from '@chakra-ui/react';

const config: ThemeConfig = {
  initialColorMode: 'dark',
  useSystemColorMode: false,
};

const customTheme = extendTheme({
  config,
  colors: {
    brand: {
      50: '#E5F4FF',
      100: '#C2E5FF',
      200: '#99D6FF',
      300: '#66C4FF',
      400: '#33B5FF',
      500: '#00A3FF',  // Primary blue
      600: '#0082CC',
      700: '#006299',
      800: '#004166',
      900: '#002033',
    },
    accent: {
      50: '#FFE5F7',
      100: '#FFB8E9',
      200: '#FF8ADB',
      300: '#FF5CCD',
      400: '#FF2EBF',
      500: '#FF00B1',  // Vibrant pink
      600: '#CC008E',
      700: '#99006A',
      800: '#660047',
      900: '#330023',
    },
    highlight: {
      50: '#FFF3E5',
      100: '#FFE1B8',
      200: '#FFCF8A',
      300: '#FFBD5C',
      400: '#FFAB2E',
      500: '#FF9900',  // Warm orange
      600: '#CC7A00',
      700: '#995C00',
      800: '#663D00',
      900: '#331F00',
    },
    gray: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#E8E8E8',
      300: '#D9D9D9',
      400: '#BFBFBF',
      500: '#8C8C8C',
      600: '#595959',
      700: '#434343',
      800: '#262626',
      900: '#141414',
    },
  },
  styles: {
    global: (props: any) => ({
      body: {
        bg: props.colorMode === 'dark' ? 'gray.900' : 'gray.50',
        color: props.colorMode === 'dark' ? 'white' : 'gray.900',
        backgroundImage: props.colorMode === 'dark'
          ? 'linear-gradient(to bottom right, gray.900, brand.900, accent.900)'
          : 'linear-gradient(to bottom right, white, brand.50, accent.50)',
        minHeight: '100vh',
      },
      '::-webkit-scrollbar': {
        width: '10px',
      },
      '::-webkit-scrollbar-track': {
        bg: props.colorMode === 'dark' ? 'gray.800' : 'gray.100',
      },
      '::-webkit-scrollbar-thumb': {
        bg: props.colorMode === 'dark' ? 'brand.700' : 'brand.500',
        borderRadius: 'full',
      },
    }),
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'brand',
      },
      variants: {
        solid: (props: any) => ({
          bg: props.colorMode === 'dark' ? 'brand.500' : 'brand.500',
          color: 'white',
          _hover: {
            bg: props.colorMode === 'dark' ? 'brand.400' : 'brand.600',
            transform: 'translateY(-2px)',
            boxShadow: 'lg',
          },
          transition: 'all 0.2s ease-in-out',
        }),
        outline: (props: any) => ({
          borderColor: props.colorMode === 'dark' ? 'brand.500' : 'brand.500',
          color: props.colorMode === 'dark' ? 'brand.500' : 'brand.500',
          _hover: {
            bg: props.colorMode === 'dark' ? 'brand.900' : 'brand.50',
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.2s ease-in-out',
        }),
      },
    },
    Card: {
      baseStyle: (props: any) => ({
        container: {
          bg: props.colorMode === 'dark' 
            ? 'rgba(26, 32, 44, 0.8)' 
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderColor: props.colorMode === 'dark' ? 'brand.700' : 'brand.200',
          borderRadius: 'xl',
          boxShadow: props.colorMode === 'dark' 
            ? '0 4px 20px rgba(0, 163, 255, 0.2)' 
            : '0 4px 20px rgba(0, 163, 255, 0.1)',
          transition: 'all 0.3s ease-in-out',
          _hover: {
            transform: 'translateY(-4px)',
            boxShadow: props.colorMode === 'dark'
              ? '0 10px 30px rgba(0, 163, 255, 0.3)'
              : '0 10px 30px rgba(0, 163, 255, 0.2)',
            borderColor: props.colorMode === 'dark' ? 'accent.600' : 'accent.300',
          },
        },
      }),
    },
    Heading: {
      baseStyle: (props: any) => ({
        color: props.colorMode === 'dark' ? 'white' : 'gray.900',
        fontWeight: '700',
      }),
    },
    Text: {
      baseStyle: (props: any) => ({
        color: props.colorMode === 'dark' ? 'gray.300' : 'gray.700',
      }),
    },
  },
  layerStyles: {
    gradientBg: {
      bgGradient: 'linear(to-r, brand.900, accent.900)',
    },
    card: {
      bg: 'gray.800',
      borderRadius: 'xl',
      boxShadow: 'xl',
      p: 6,
    },
  },
  textStyles: {
    h1: {
      fontSize: ['4xl', '5xl'],
      fontWeight: 'bold',
      lineHeight: '110%',
      letterSpacing: '-2%',
    },
    h2: {
      fontSize: ['3xl', '4xl'],
      fontWeight: 'semibold',
      lineHeight: '110%',
      letterSpacing: '-1%',
    },
  },
});

export default customTheme;
